<template>
    <aside class="cal-lib" :class="{ 'cal-lib--open': value, 'cal-lib--drop': dropActive }" :style="drawerStyle">
        <div class="cal-lib-hdr">
            <span class="cal-lib-title">{{ $t('common.library') }}</span>
            <button-tooltip bottom :tooltip="$t('common.close')">
                <template #activator="{ on, attrs }">
                    <button type="button" class="cal-lib-close" v-bind="attrs" v-on="on" @click="$emit('input', false)">
                        <v-icon size="14">mdi-close</v-icon>
                    </button>
                </template>
            </button-tooltip>
        </div>
        <div class="cal-lib-sub">{{ $t('calendar_library.drawer_hint') }}</div>

        <div class="cal-lib-tabs">
            <button v-for="item in tabs" :key="item.value" type="button" class="cal-lib-tab"
                :class="{ active: tab === item.value }" @click="tab = item.value">
                {{ item.label }}
            </button>
        </div>
        <div class="cal-lib-search">
            <kimsa-text-field :value="search" @change="search = $event" dense clearable no-elevation
                :placeholder="$t('common.search')" prepend-inner-icon="mdi-magnify" />
        </div>

        <div class="cal-lib-scroll">
            <skeleton-loader v-if="loading" type="agenda-cards" :count="4" />
            <div v-else-if="!items.length" class="cal-lib-empty">
                {{ search ? $t('common.no_results') : $t(`common.no_most_${tab}_used`) }}
            </div>
            <template v-else>
                <div v-for="item in items" :key="item.objectID" class="cal-lib-card"
                    :class="{ 'cal-lib-card--new': newIds.includes(item.objectID) }"
                    @mousedown.left.prevent="onMouseDown(item, $event)">
                    <span v-if="tab === 'programs' || tab === 'routines'" class="cal-lib-letter">
                        {{ tab === 'programs' ? 'P' : 'R' }}
                    </span>
                    <span v-else class="cal-lib-icon" :class="`cal-lib-icon--${tab}`">
                        <v-icon size="17">{{ tab === 'blocks' ? 'mdi-view-dashboard-outline' : 'mdi-check-circle-outline' }}</v-icon>
                    </span>
                    <div class="cal-lib-info">
                        <div class="cal-lib-name">
                            {{ item.name }}
                            <span v-if="newIds.includes(item.objectID)" class="cal-lib-pill">{{ $t('common.new') }}</span>
                        </div>
                        <div class="cal-lib-meta">{{ metaFor(item) }}</div>
                    </div>
                    <v-icon size="16" class="cal-lib-grip">mdi-drag-vertical</v-icon>
                </div>
            </template>
        </div>

        <div class="cal-lib-drop">
            <template v-if="dropActive && dropLabel">{{ $t('calendar_library.drop_to_save', { type: dropLabel }) }}</template>
            <template v-else>{{ $t('calendar_library.save_zone') }}</template>
        </div>
    </aside>
</template>

<script>
import { db } from '@/utils/Firebase'
import { BlockIndex, PrescriptionIndex, ProgramIndex, RoutineIndex } from '@/utils/Algolia'
import { debounce } from '@/utils/Common'
import { exerciseSetsLabel } from '@/utils/Exercises'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import KimsaTextField from '@/components/common/inputs/KimsaTextField'
import SkeletonLoader from '@/components/common/SkeletonLoader'

const INDEXES = {
    programs: ProgramIndex,
    routines: RoutineIndex,
    blocks: BlockIndex,
    prescriptions: PrescriptionIndex,
}

const DRAG_THRESHOLD = 5

export default {
    name: 'CalendarLibraryDrawer',
    components: { ButtonTooltip, KimsaTextField, SkeletonLoader },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        coachId: {
            type: String,
            default: null,
        },
        top: {
            type: Number,
            default: 0,
        },
        dropActive: {
            type: Boolean,
            default: false,
        },
        dropLabel: {
            type: String,
            default: '',
        },
        newIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            tab: 'programs',
            search: '',
            items: [],
            loading: false,
            loadedKey: null,
        }
    },
    computed: {
        tabs(vm = this) {
            return [
                { value: 'programs', label: vm.$t('common.programs') },
                { value: 'routines', label: vm.$t('common.routines') },
                { value: 'blocks', label: vm.$tc('common.blocks', 2) },
                { value: 'prescriptions', label: vm.$t('common.prescriptions') },
            ]
        },
        drawerStyle(vm = this) {
            return { top: `${vm.top}px` }
        },
    },
    methods: {
        async load(force = false) {
            const vm = this
            if (!vm.value || !vm.coachId) return
            const key = `${vm.tab}|${vm.search}|${vm.coachId}`
            if (!force && key === vm.loadedKey) return
            vm._cache = force ? {} : (vm._cache || {})
            vm.loadedKey = key
            if (vm._cache[key]) {
                vm.items = vm._cache[key]
                vm.loading = false
                vm.sortNewFirst()
                return
            }
            vm.loading = true
            try {
                if (vm.search) {
                    const res = await INDEXES[vm.tab].search(vm.search, { filters: `coachId: ${vm.coachId}`, hitsPerPage: 30 })
                    vm._cache[key] = res.hits
                    if (key === vm.loadedKey) vm.items = res.hits
                } else {
                    const snap = await db.collection(vm.tab)
                        .where('coachId', '==', vm.coachId)
                        .orderBy('used', 'desc')
                        .limit(30)
                        .get()
                    vm._cache[key] = snap.docs.map(doc => ({ ...doc.data(), id: doc.id, objectID: doc.id, fromFirestore: true }))
                    if (key === vm.loadedKey) vm.items = vm._cache[key]
                }
                if (key === vm.loadedKey) vm.sortNewFirst()
            } catch (error) {
                console.error('[calendar library]', error)
                vm.items = []
            } finally {
                if (key === vm.loadedKey) vm.loading = false
            }
        },
        sortNewFirst() {
            const vm = this
            if (!vm.newIds.length) return
            vm.items = [...vm.items].sort((a, b) => Number(vm.newIds.includes(b.objectID)) - Number(vm.newIds.includes(a.objectID)))
        },
        async showTab(tab) {
            const vm = this
            vm.search = ''
            vm.tab = tab
            await vm.load(true)
        },
        debouncedLoad: debounce(function () {
            this.load()
        }, 400),
        metaFor(item) {
            const vm = this
            const parts = []
            if (vm.tab === 'programs') {
                const sessions = item.assignedRoutines?.length || 0
                const perWeek = Number(item.daysPerWeek) || 1
                if (sessions) parts.push(vm.$t('calendar_library.weeks_short', { n: Math.ceil(sessions / perWeek) }))
                parts.push(vm.$t('calendar_library.per_week_short', { n: perWeek }))
                if (item.category) parts.push(vm.routineObjectiveLabel(item.category) || item.category)
            } else if (vm.tab === 'routines') {
                const blocks = item.blocks?.length || item.description?.blocks || 0
                const exercises = (item.blocks || []).reduce((total, block) => total + (block.exercises?.length || 0), 0)
                parts.push(`${blocks} ${vm.$tc('common.blocks', blocks).toLowerCase()}`)
                if (exercises) parts.push(`${exercises} ${vm.$tc('common.exercises', exercises).toLowerCase()}`)
            } else if (vm.tab === 'blocks') {
                const exercises = item.block?.exercises?.length || 0
                parts.push(`${exercises} ${vm.$tc('common.exercises', exercises).toLowerCase()}`)
            } else {
                const label = exerciseSetsLabel({ config: item.config }, null, vm.$t)
                if (label) parts.push(label)
            }
            return parts.filter(Boolean).join(' · ')
        },
        onMouseDown(item, event) {
            const vm = this
            const start = { x: event.clientX, y: event.clientY }
            const type = vm.tab
            const move = (e) => {
                if (Math.abs(e.clientX - start.x) < DRAG_THRESHOLD && Math.abs(e.clientY - start.y) < DRAG_THRESHOLD) return
                cleanup()
                vm.$emit('drag-start', { type, item }, e)
            }
            const up = () => {
                cleanup()
                vm.$emit('item-click', { type, item })
            }
            const cleanup = () => {
                window.removeEventListener('mousemove', move)
                window.removeEventListener('mouseup', up)
            }
            window.addEventListener('mousemove', move)
            window.addEventListener('mouseup', up)
        },
    },
    watch: {
        value(val) {
            if (val) this.load()
        },
        tab() {
            this.load()
        },
        search() {
            this.debouncedLoad()
        },
        coachId() {
            this.load(true)
        },
        newIds() {
            this.sortNewFirst()
        },
    },
}
</script>

<style scoped>
.cal-lib {
    position: fixed;
    right: 0;
    bottom: 0;
    width: 340px;
    z-index: 5;
    display: flex;
    flex-direction: column;
    background: var(--v-surface-base);
    border-left: 1px solid var(--v-outline-base);
    border-top: 1px solid var(--v-outline-base);
    transform: translateX(100%);
    visibility: hidden;
    transition: transform .22s cubic-bezier(.4, 0, .2, 1), visibility .22s;
}

.cal-lib--open {
    transform: none;
    visibility: visible;
}

.cal-lib--drop {
    box-shadow: inset 0 0 0 2px var(--v-accent-base);
}

.theme--dark .cal-lib--drop {
    box-shadow: inset 0 0 0 2px var(--v-primary-base);
}

.cal-lib-hdr {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 14px 6px;
}

.cal-lib-title {
    flex: 1;
    font-size: 15px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.cal-lib-close {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
}

.cal-lib-close .v-icon {
    color: inherit !important;
}

.cal-lib-sub {
    padding: 0 14px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--v-txtMuted-base);
}

.cal-lib-tabs {
    display: flex;
    gap: 2px;
    margin: 12px 12px 8px;
    padding: 3px;
    border-radius: 9px;
    background: var(--v-surface2-base);
    flex-shrink: 0;
}

.cal-lib-tab {
    flex: 1;
    padding: 6px 2px;
    border-radius: 7px;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
    color: var(--v-txtMuted-base);
    transition: all .15s;
}

.cal-lib-tab:hover {
    color: var(--v-txtSecondary-base);
}

.cal-lib-tab.active {
    background: var(--v-surface-base);
    color: var(--v-txtPrimary-base);
    box-shadow: 0 1px 3px rgba(0, 0, 0, .18);
}

.cal-lib-search {
    padding: 0 12px 8px;
    flex-shrink: 0;
}

.cal-lib-scroll {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 2px 0 8px;
}

.cal-lib-empty {
    padding: 18px 14px;
    font-size: 12px;
    text-align: center;
    color: var(--v-txtMuted-base);
}

.cal-lib-card {
    margin: 2px 10px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
    cursor: grab;
    user-select: none;
    transition: border-color .15s, background .15s;
}

.cal-lib-card:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface2-base);
}

.cal-lib-card:active {
    cursor: grabbing;
}

.cal-lib-card--new {
    border-color: rgba(51, 153, 153, .45);
}

.theme--dark .cal-lib-card--new {
    border-color: rgba(204, 255, 51, .45);
}

.cal-lib-letter,
.cal-lib-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.cal-lib-letter {
    font-size: 16px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    background: linear-gradient(135deg, rgba(51, 153, 153, .2), rgba(204, 255, 51, .08));
    border: 1px solid var(--v-outline-base);
}

.cal-lib-icon--blocks {
    background: rgba(51, 153, 153, .15);
}

.cal-lib-icon--blocks .v-icon {
    color: var(--v-accent-base) !important;
}

.cal-lib-icon--prescriptions {
    background: rgba(0, 153, 102, .15);
}

.cal-lib-icon--prescriptions .v-icon {
    color: var(--v-success-base) !important;
}

.cal-lib-info {
    flex: 1;
    min-width: 0;
}

.cal-lib-name {
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--v-txtPrimary-base);
}

.cal-lib-pill {
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 10px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--v-accent-base);
    background: rgba(51, 153, 153, .12);
}

.theme--dark .cal-lib-pill {
    color: var(--v-primary-base);
    background: rgba(204, 255, 51, .1);
}

.cal-lib-meta {
    font-size: 10px;
    font-weight: 500;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cal-lib-grip {
    flex-shrink: 0;
    color: var(--v-txtMuted-base) !important;
}

.cal-lib-drop {
    margin: 8px 12px 14px;
    padding: 14px 12px;
    flex-shrink: 0;
    border: 1.5px dashed var(--v-outline2-base);
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.45;
    text-align: center;
    color: var(--v-txtMuted-base);
    transition: border-color .15s, color .15s, background .15s;
}

.cal-lib--drop .cal-lib-drop {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
    background: rgba(51, 153, 153, .06);
}

.theme--dark .cal-lib--drop .cal-lib-drop {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
    background: rgba(204, 255, 51, .06);
}
</style>
